/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import * as workItemsApi from "../api/workItems";
import WorkItemEditDrawer from
  "../components/work-items/WorkItemEditDrawer";

const ACTIVE_STATUSES = new Set([
  "open",
  "in_progress",
  "blocked",
  "complete",
]);

export default function MyWorkItems() {
  const [workItems, setWorkItems] = useState([]);
  const [selectedWorkItem, setSelectedWorkItem] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] =
    useState("active");

  const loadWorkItems = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const result =
        await workItemsApi.getMyWorkItems();

      setWorkItems(
        Array.isArray(result)
          ? result
          : result?.items || []
      );
    } catch (err) {
      console.error(err);

      setError(
        err?.response?.data?.detail ||
          "Failed to load your work items."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkItems();
  }, [loadWorkItems]);

  const counts = useMemo(() => {
    return workItems.reduce(
      (result, workItem) => {
        const status = workItem.status;

        result.total += 1;

        if (status === "open") {
          result.open += 1;
        }

        if (status === "in_progress") {
          result.inProgress += 1;
        }

        if (status === "blocked") {
          result.blocked += 1;
        }

        if (
          status === "complete" &&
          !workItem.verified_at
        ) {
          result.awaitingVerification += 1;
        }

        if (
          status === "verified" ||
          workItem.verified_at
        ) {
          result.verified += 1;
        }

        if (ACTIVE_STATUSES.has(status)) {
          result.active += 1;
        }

        return result;
      },
      {
        total: 0,
        active: 0,
        open: 0,
        inProgress: 0,
        blocked: 0,
        awaitingVerification: 0,
        verified: 0,
      }
    );
  }, [workItems]);

  const filteredWorkItems = useMemo(() => {
    if (statusFilter === "all") {
      return workItems;
    }

    if (statusFilter === "active") {
      return workItems.filter((workItem) =>
        ACTIVE_STATUSES.has(workItem.status)
      );
    }

    if (statusFilter === "awaiting_verification") {
      return workItems.filter(
        (workItem) =>
          workItem.status === "complete" &&
          !workItem.verified_at
      );
    }

    if (statusFilter === "verified") {
      return workItems.filter(
        (workItem) =>
          workItem.status === "verified" ||
          Boolean(workItem.verified_at)
      );
    }

    return workItems.filter(
      (workItem) =>
        workItem.status === statusFilter
    );
  }, [statusFilter, workItems]);

  function openWorkItem(workItem) {
    setSelectedWorkItem(workItem);
  }

  function closeWorkItem() {
    setSelectedWorkItem(null);
  }

  function handleWorkItemUpdated(updatedWorkItem) {
    setWorkItems((current) =>
      current.map((workItem) =>
        workItem.id === updatedWorkItem.id
          ? updatedWorkItem
          : workItem
      )
    );

    setSelectedWorkItem(updatedWorkItem);
  }

  return (
    <div className="px-6 lg:px-8 py-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            My Work Items
          </h1>

          <p className="mt-1 text-slate-500">
            Corrective actions assigned to you or
            one of your groups.
          </p>
        </div>

        <button
          type="button"
          onClick={loadWorkItems}
          disabled={loading}
          className="self-start rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <Metric
          label="Active"
          value={counts.active}
          active={statusFilter === "active"}
          onClick={() =>
            setStatusFilter("active")
          }
        />

        <Metric
          label="Open"
          value={counts.open}
          active={statusFilter === "open"}
          onClick={() =>
            setStatusFilter("open")
          }
        />

        <Metric
          label="In Progress"
          value={counts.inProgress}
          active={statusFilter === "in_progress"}
          onClick={() =>
            setStatusFilter("in_progress")
          }
        />

        <Metric
          label="Blocked"
          value={counts.blocked}
          active={statusFilter === "blocked"}
          tone="danger"
          onClick={() =>
            setStatusFilter("blocked")
          }
        />

        <Metric
          label="Awaiting Verification"
          value={counts.awaitingVerification}
          active={
            statusFilter ===
            "awaiting_verification"
          }
          tone="warning"
          onClick={() =>
            setStatusFilter(
              "awaiting_verification"
            )
          }
        />
      </section>

      {error ? (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          Loading work items...
        </div>
      ) : null}

      {!loading &&
      filteredWorkItems.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center dark:border-slate-700 dark:bg-slate-900">
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            No work items match this filter
          </div>

          <p className="mt-1 text-sm text-slate-500">
            Work items assigned to you will appear
            here.
          </p>
        </div>
      ) : null}

      {!loading &&
      filteredWorkItems.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          {filteredWorkItems.map(
            (workItem, index) => (
              <WorkItemRow
                key={workItem.id}
                workItem={workItem}
                isLast={
                  index ===
                  filteredWorkItems.length - 1
                }
                onClick={() =>
                  openWorkItem(workItem)
                }
              />
            )
          )}
        </div>
      ) : null}

      <WorkItemEditDrawer
        workItem={selectedWorkItem}
        onClose={closeWorkItem}
        onUpdated={handleWorkItemUpdated}
      />
    </div>
  );
}

function Metric({
  label,
  value,
  active,
  tone = "default",
  onClick,
}) {
  const toneClasses = {
    default:
      "text-slate-900 dark:text-white",
    danger:
      "text-red-600 dark:text-red-400",
    warning:
      "text-amber-600 dark:text-amber-400",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-xl border bg-white p-5 text-left shadow-sm transition",
        "hover:-translate-y-0.5 hover:shadow-md",
        "dark:bg-slate-900",
        active
          ? "border-primary ring-2 ring-primary/10"
          : "border-slate-200 dark:border-slate-800",
      ].join(" ")}
    >
      <div
        className={[
          "text-2xl font-bold",
          toneClasses[tone],
        ].join(" ")}
      >
        {value}
      </div>

      <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
    </button>
  );
}

function WorkItemRow({
  workItem,
  isLast,
  onClick,
}) {
  const overdue = isOverdue(workItem);

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex w-full items-center justify-between gap-5 px-5 py-5 text-left transition",
        "hover:bg-slate-50 dark:hover:bg-slate-800/60",
        !isLast
          ? "border-b border-slate-200 dark:border-slate-800"
          : "",
      ].join(" ")}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-bold text-slate-900 dark:text-white">
            {workItem.title}
          </h3>

          <StatusBadge workItem={workItem} />
        </div>

        {workItem.description ? (
          <p className="mt-2 line-clamp-2 text-sm text-slate-500">
            {workItem.description}
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
          {workItem.incident_number ? (
            <span className="font-semibold text-slate-600 dark:text-slate-300">
              {workItem.incident_number}
            </span>
          ) : null}

          {workItem.priority ? (
            <span>
              Priority:{" "}
              {formatLabel(workItem.priority)}
            </span>
          ) : null}

          {workItem.due_date ? (
            <span
              className={
                overdue
                  ? "font-semibold text-red-600 dark:text-red-400"
                  : ""
              }
            >
              Due {formatDate(workItem.due_date)}
            </span>
          ) : null}
        </div>
      </div>

      <span
        aria-hidden="true"
        className="shrink-0 text-2xl text-slate-300 dark:text-slate-600"
      >
        ›
      </span>
    </button>
  );
}

function StatusBadge({ workItem }) {
  const status = workItem.verified_at
    ? "verified"
    : workItem.status;

  const classes = {
    open:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    in_progress:
      "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
    blocked:
      "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
    complete:
      "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    verified:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  };

  return (
    <span
      className={[
        "rounded-full px-2.5 py-1 text-xs font-semibold",
        classes[status] || classes.open,
      ].join(" ")}
    >
      {formatStatus(workItem)}
    </span>
  );
}

function formatStatus(workItem) {
  if (workItem.verified_at) {
    return "Verified";
  }

  if (workItem.status === "complete") {
    return "Awaiting Verification";
  }

  return formatLabel(workItem.status);
}

function formatLabel(value) {
  if (!value) {
    return "Unknown";
  }

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function formatDate(value) {
  if (!value) {
    return "";
  }

  return new Intl.DateTimeFormat(
    undefined,
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  ).format(new Date(value));
}

function isOverdue(workItem) {
  if (
    !workItem.due_date ||
    workItem.status === "verified" ||
    workItem.verified_at
  ) {
    return false;
  }

  const dueDate = new Date(workItem.due_date);
  dueDate.setHours(23, 59, 59, 999);

  return dueDate < new Date();
}